import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { DevicesStore, InstallationFeaturesStore } from '@store';
import {
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  IsNumberValueProperty,
  IsStringValueProperty,
  PowerLimitationOverviewProperties,
  PowerLimitationType,
  SmartGridCommands,
  SmartGridProperties,
} from '@vi-iot/client/index';
import { filter, Observable, Subject, takeUntil } from 'rxjs';
import { SharedDependenciesModule } from 'src/app/shared';
import { DryContactSmartGridPipe } from './pipes/dry-contact-smart-grid.pipe';
import { DryContactSgReadyInfoPipe } from './pipes/dry-contact-sg-ready-info.pipe';
import { DryContactConsumptionStatusPipe } from './pipes/dry-contact-consumption-status.pipe';
import { FeaturesDetailsOverlayTypeEnum } from '../../installation-overlay.model';
import { PillType } from 'src/app/shared/components/pill/pill.model';

@Component({
  selector: 'app-dry-contact-feature-tiles',
  standalone: true,
  imports: [
    SharedDependenciesModule,
    DryContactSmartGridPipe,
    DryContactSgReadyInfoPipe,
    DryContactConsumptionStatusPipe,
  ],
  templateUrl: './dry-contact-feature-tiles.component.html',
  styleUrl: './dry-contact-feature-tiles.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DryContactFeatureTilesComponent implements OnInit, OnDestroy {
  readonly FeaturePropertyKey = FeaturePropertyKey;
  readonly FeaturesDetailsOverlayTypeEnum = FeaturesDetailsOverlayTypeEnum;
  readonly PillType = PillType;

  smartGrid$: Observable<Feature<SmartGridProperties, SmartGridCommands>>;
  dryContactPowerConsumption$: Observable<Feature<IsNumberValueProperty, unknown>>;
  dryContactConsumptionStatus$: Observable<Feature<IsStringValueProperty, unknown>>;
  powerLimitationOverview$: Observable<Feature<PowerLimitationOverviewProperties, unknown>>;

  isConsumptionLimitation: boolean;
  isProductionLimitation: boolean;

  private ngUnsubscribe$: Subject<void> = new Subject();

  constructor(
    private installationFeaturesStore: InstallationFeaturesStore,
    private devicesStore: DevicesStore
  ) {}
  ngOnInit(): void {
    this.devicesStore.dryContactHeatPump$
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter((device) => !!device)
      )
      .subscribe((device) => {
        this.setFeatureObservables(device.id);
      });
  }

  private setFeatureObservables(deviceId: string): void {
    this.smartGrid$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatPumpSmartGrid,
      deviceId
    );

    this.dryContactPowerConsumption$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dryContactInstantaneousPower,
      deviceId
    );

    this.dryContactConsumptionStatus$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dryContactPowerConsumptionStatus,
      deviceId
    );

    this.powerLimitationOverview$ = this.installationFeaturesStore.selectInstallationGatewayFeature(
      FeatureNames.powerLimitationOverview
    );

    this.powerLimitationOverview$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((feature) => {
      const featureLimitations = feature?.properties?.limitations?.value ?? [];
      this.isConsumptionLimitation = featureLimitations.some(
        (el) => el.limitationType === PowerLimitationType.consumption
      );
      this.isProductionLimitation = featureLimitations.some(
        (el) => el.limitationType === PowerLimitationType.production
      );
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
